import { Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import * as Yup from 'yup';

import {
  Accordion, AccordionDetails, AccordionSummary, Autocomplete, Button, CircularProgress,
  FormHelperText, IconButton, Radio, RadioGroup, TextField
} from '@mui/material';
import PaystackPop from '@paystack/inline-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { BlackCaretDownIcon, CloseCircleIcon } from '../../../../../assets/svg';
import { PyButton, PyInput, PySelect, TOASTER_TYPE, ToastMessage } from '../../../../../components';
import { InputBox } from '../../../../../components/PyInputs/input/input.styles';
import { cardBrands, cardTypes, images } from '../../../../../constant';
import { urlConfig } from '../../../config';
import { usePayLinkCart } from '../../../context/PayLinkCartContext';
import { TPayLinkCardToken, usePayLinkUser } from '../../../context/PaylinkUserContext';
import {
  createPayLinkCardToken, getBanks, getCardToken, getGatewaySettings, initPayLinkTransaction,
  removePayLinkCardToken, verifyBankAccountNumber, verifyPayLinkTransaction
} from '../../../services/api';
import {
  createStripeCustomer, createStripeSetupIntent, retrieveStripeCustomer
} from '../../../../../services/api';
import {
  IPaystackEventResponse, IVerificationRequest, IVerificationResponse
} from '../../../types/payments';
import { AddPayLinkStripePaymentMethod } from './AddMethod/Stripe';

const validationSchema = Yup.object().shape({
  bankName: Yup.string().required("Bank name is required, select a bank"),
  accountNumber: Yup.string().required("Account number is required"),
  cardType: Yup.string().required("Card type is required"),
  brand: Yup.string().required("Card brand is required, select card brand"),
  bin: Yup.string()
    .required("Enter first 6 digits of your card")
    .max(6, "Maximum 6 digits"),
});

const paystackCheckoutUrl = "https://checkout.paystack.com";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "",
);

export const PaymentMethod = ({ isDisabled = false }: { isDisabled?: boolean }) => {
  const [expand, setExpand] = useState<boolean>(true);
  const [showAdd, setShowAdd] = useState<boolean>(false);
  const [selectedTokenUrn, setSelectedTokenUrn] = useState<string>("");
  const [cardVerificationDetails, setCardVerificationDetails] = useState<{
    bankName: string;
    accountNumber: string;
    accountName: string;
    cardType: string;
    brand: string;
    bin: string;
    country: string;
    verificationRequired: boolean;
  } | null>(null);
  const [verifyData, setVerifyData] = useState<{
    accountNumber: string;
    bankCode: string;
  } | null>(null);

  const { storeProfile, gatewaySetting, setGatewaySetting } = usePayLinkCart();
  const {
    user,
    cardToken: selectedCardToken,
    setCardToken: setSelectedCardToken,
    shippingAddress,
  } = usePayLinkUser();
  const queryClient = useQueryClient();
  const [removingTokenUrn, setRemovingTokenUrn] = useState<string | null>(null);
  const toggleCardExpand = () => setExpand(!expand);
  const initialValues = {
    bankName: "",
    accountNumber: "",
    accountName: "",
    cardType: "",
    brand: "",
    bin: "",
    country: "Nigeria",
    verificationRequired: true,
  };

  // ── Card tokens ───────────────────────────────────────────────────────────
  const { data: tokenResponse, isLoading: isTokenLoading, refetch } = useQuery({
    queryKey: [
      "PayLinkCardTokens",
      storeProfile?.customerUniqueReferenceNumber,
      user?.consumerUniqueReferenceNumber,
      user?.walletUniqueReferenceNumber,
    ],
    queryFn: () =>
      getCardToken({
        customerUniqueReferenceNumber:
          storeProfile?.customerUniqueReferenceNumber,
        merchantUniqueReferenceNumber:
          storeProfile?.merchantUniqueReferenceNumber,
        consumerUniqueReferenceNumber: user?.consumerUniqueReferenceNumber,
        walletUniqueReferenceNumber: user?.walletUniqueReferenceNumber,
      }),
    enabled:
      !!storeProfile?.customerUniqueReferenceNumber &&
      !!user?.consumerUniqueReferenceNumber &&
      !!user?.walletUniqueReferenceNumber,
    refetchOnWindowFocus: false,
  });

  const methods = tokenResponse?.data ?? [];

  // ── Payment gateway ───────────────────────────────────────────────────────
  const { data: gateway_data } = useQuery({
    queryKey: ["PayLinkGateway", storeProfile?.customerUniqueReferenceNumber],
    queryFn: () =>
      getGatewaySettings({
        customerUniqueReferenceNumber:
          storeProfile!.customerUniqueReferenceNumber,
      }),
    enabled: !!storeProfile?.customerUniqueReferenceNumber,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (gateway_data && gateway_data.length > 0) {
      const _live_gateway_setting =
        gateway_data.find((g) => g.environment === "Live") ?? null;
      setGatewaySetting(_live_gateway_setting);
    }
  }, [gateway_data]);

  const isPaystack =
    gatewaySetting?.paymentGatewayType?.toLowerCase() === "paystack";

  const isStripe =
    gatewaySetting?.paymentGatewayType?.toLowerCase() === "stripe";

  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripeIntentData, setStripeIntentData] = useState<any>(null);
  const [stripeCustomer, setStripeCustomer] = useState<any>(null);
  const [isCustomerLoading, setIsCustomerLoading] = useState<boolean>(false);

  const {
    data: stripeCustomerData,
    isLoading: stripeCustomerIsLoading,
    isFetched: stripeCustomerIsFetched,
  } = useQuery({
    queryKey: ["GetPayLinkStripeCustomer", user?.consumerUniqueReferenceNumber],
    queryFn: () =>
      retrieveStripeCustomer({
        customerUniqueReferenceNumber:
          storeProfile?.customerUniqueReferenceNumber,
        consumerUniqueReferenceNumber:
          user?.consumerUniqueReferenceNumber,
      }),
    enabled:
      isStripe &&
      !!storeProfile?.customerUniqueReferenceNumber &&
      !!user?.consumerUniqueReferenceNumber,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // Effect to handle customer creation if no existing customer found
  useEffect(() => {
    const handleCustomerSetup = async () => {
      if (!isStripe || !stripeCustomerIsFetched || !user) return;

      // If customer already exists from search, use it
      if (stripeCustomerData?.customer) {
        setStripeCustomer(stripeCustomerData.customer);
        return;
      }

      // No customer found, create a new one
      setIsCustomerLoading(true);
      try {
        const newCustomer = await createStripeCustomer({
          email: user.email,
          name: user.consumerUniqueReferenceNumber,
          metadata: {
            consumerUniqueReferenceNumber:
              user.consumerUniqueReferenceNumber,
            customerUniqueReferenceNumber:
              storeProfile?.customerUniqueReferenceNumber,
          },
          customerUniqueReferenceNumber:
            storeProfile?.customerUniqueReferenceNumber,
        });

        if (newCustomer?.id) {
          setStripeCustomer(newCustomer);
        }
      } catch (error) {
        console.error("Error creating Stripe customer:", error);
      } finally {
        setIsCustomerLoading(false);
      }
    };

    handleCustomerSetup();
  }, [stripeCustomerIsFetched, stripeCustomerData, user, isStripe, storeProfile]);

  const { data: stripeData, isLoading: isStripeLoading } = useQuery({
    queryKey: ["PayLinkStripeSetupIntent", stripeCustomer?.id],
    queryFn: () =>
      createStripeSetupIntent({
        customerUniqueReferenceNumber:
          storeProfile?.customerUniqueReferenceNumber,
        customerEmail: user?.email,
        consumerUniqueReferenceNumber:
          user?.consumerUniqueReferenceNumber,
        customerId: stripeCustomer?.id,
      }),
    enabled:
      (showAdd || methods.length === 0) &&
      isStripe &&
      !!stripeCustomer?.id &&
      !!storeProfile?.customerUniqueReferenceNumber,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (stripeData) {
      const clientSecret =
        stripeData?.clientSecret || stripeData?.client_secret;
      const intentId = stripeData?.setupIntentId || stripeData?.paymentIntentId;

      if (clientSecret) {
        setStripeClientSecret(clientSecret);
        setStripeIntentData({ ...stripeData, clientSecret, intentId });
      }
    }
  }, [stripeData]);

  // ── Banks ─────────────────────────────────────────────────────────────────
  const { data: bankData } = useQuery({
    queryKey: ["PayLinkBanks", storeProfile?.customerUniqueReferenceNumber],
    queryFn: () =>
      getBanks({
        country: "Nigeria",
        gateway: "PAYSTACK",
        customerUniqueReferenceNumber:
          storeProfile!.customerUniqueReferenceNumber,
      }),
    enabled: !!storeProfile?.customerUniqueReferenceNumber && isPaystack,
    staleTime: Infinity,
  });

  const allBanks = useMemo(() => {
    const banks = bankData?.data || [];
    return Array.isArray(banks)
      ? banks
        .map((b) => ({ ...b, value: b.code }))
        .sort((a, b) => a.name.localeCompare(b.name))
      : [];
  }, [bankData]);

  // ── Account verification ──────────────────────────────────────────────────
  const { data: verifyAccountData, isFetched: isAccountVerified } = useQuery({
    queryKey: [
      "PayLinkVerifyAccount",
      verifyData?.accountNumber,
      verifyData?.bankCode,
    ],
    queryFn: () =>
      verifyBankAccountNumber({
        accountNumber: verifyData!.accountNumber,
        bankCode: verifyData!.bankCode,
        gateway: "PAYSTACK",
        customerUniqueReferenceNumber:
          storeProfile!.customerUniqueReferenceNumber,
      }),
    enabled: !!verifyData?.accountNumber && !!verifyData?.bankCode,
    staleTime: 0,
    retry: 0,
  });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createTokenMutation = useMutation(createPayLinkCardToken, {
    onSuccess: (res) => {
      setShowAdd(false);
      queryClient.invalidateQueries({
        queryKey: ["PayLinkCardTokens"],
      });
      localStorage.removeItem("P_D");
      ToastMessage(TOASTER_TYPE.SUCCESS, "Card added successfully");
    },
    onError: () => {
      ToastMessage(TOASTER_TYPE.ERROR, "Failed to add card. Please try again.");
    },
  });

  const removeTokenMutation = useMutation({
    mutationFn: removePayLinkCardToken,
    onSuccess: (_res, variables) => {
      if (selectedCardToken?.uniqueReferenceNumber === variables.uniqueReferenceNumber) {
        setSelectedCardToken(null);
        setSelectedTokenUrn("");
      }
      queryClient.invalidateQueries({ queryKey: ["PayLinkCardTokens"] });
      ToastMessage(TOASTER_TYPE.SUCCESS, "Card removed successfully");
    },
    onError: () => {
      ToastMessage(TOASTER_TYPE.ERROR, "Unable to remove card. Please try again.");
    },
    onSettled: () => {
      setRemovingTokenUrn(null);
    },
  });

  const handleRemoveToken = (token: TPayLinkCardToken) => {
    if (removeTokenMutation.isLoading) return;
    setRemovingTokenUrn(token.uniqueReferenceNumber);
    removeTokenMutation.mutate({
      uniqueReferenceNumber: token.uniqueReferenceNumber,
      customerUniqueReferenceNumber: token.customerUniqueReferenceNumber,
      walletUniqueReferenceNumber: token.walletUniqueReferenceNumber,
      gatewayCustomerId: token.gatewayCustomerId,
      emailAddress: token.emailAddress,
      paymentMethodUniqueReferenceNumber: token.paymentMethodUniqueReferenceNumber,
    });
  };

  const verifyTxMutation = useMutation(verifyPayLinkTransaction, {
    onSuccess: (res) => {
      handleCreateCardToken(res);
    },
    onError: () => {
      ToastMessage(
        TOASTER_TYPE.ERROR,
        "Verification failed. Please try again.",
      );
    },
  });

  const initTxMutation = useMutation(initPayLinkTransaction, {
    onSuccess: async (res) => {
      console.log("res", res);
      localStorage.setItem("P_D", JSON.stringify(res));
      await handlePaystackResume(res?.authorizationCode);
    },
    onError: () => {
      ToastMessage(
        TOASTER_TYPE.ERROR,
        "Failed to start payment. Please try again.",
      );
    },
  });

  // ── Paystack inline message listener ─────────────────────────────────────
  useEffect(() => {
    const messageListener = (event: MessageEvent) => {
      if (event.origin !== paystackCheckoutUrl) return;
      if (event.data?.event === "success") {
        handleVerifyTransaction({
          ...event.data.data,
          customerUniqueReferenceNumber:
            storeProfile?.customerUniqueReferenceNumber,
        });
      }
    };
    window.addEventListener("message", messageListener);
    return () => window.removeEventListener("message", messageListener);
  }, [storeProfile]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleTokenChange = (value: string) => {
    setSelectedTokenUrn(value);
    const token = methods.find((item) => item.uniqueReferenceNumber === value);
    setSelectedCardToken(token || null);
  };

  const getBankCode = (bankName: string) => {
    const selected = allBanks.find((b) => b.name === bankName);
    return selected?.code as string | undefined;
  };

  const handleVerifyAccountNumber = (
    bankName: string,
    accountNumber: string,
  ) => {
    if (!bankName || accountNumber.length < 10) return;
    const bankCode = getBankCode(bankName);
    if (!bankCode) return;
    setVerifyData({
      accountNumber,
      // if dev use 001 and if pro use the actual bank code from the list of banks retrieved from paystack
      bankCode: urlConfig.IS_BANK_VERIFICATION_DEV_ENV ? "001" : bankCode,
    });
  };

  const handlePaystackResume = async (accessCode: string) => {
    const paystack = new PaystackPop();
    try {
      await paystack.resumeTransaction(accessCode);
    } catch (err) {
      console.error("Paystack popup error:", err);
    }
  };

  const handleVerifyTransaction = (
    paystackResponse: IPaystackEventResponse,
  ) => {
    const pdData = localStorage.getItem("P_D");
    if (!pdData) return;
    const parsed = JSON.parse(pdData);

    verifyTxMutation.mutate({
      uniqueReferenceNumber: parsed.uniqueReferenceNumber,
      paymentUniqueReferenceNumber: parsed.paymentUniqueReferenceNumber,
      paymentItemUniqueReferenceNumber: parsed.paymentItemUniqueReferenceNumber,
      paymentMethodVerification: true,
      paymentGatewayReferenceNumber: paystackResponse.reference,
      customerUniqueReferenceNumber:
        parsed.customerUniqueReferenceNumber ||
        storeProfile?.customerUniqueReferenceNumber ||
        "",
    });
  };

  const handleCreateCardToken = (verificationData: IVerificationResponse) => {
    if (!storeProfile || !user || !cardVerificationDetails || !shippingAddress)
      return;
    createTokenMutation.mutate({
      merchantUniqueReferenceNumber:
        verificationData?.customerUniqueReferenceNumber ||
        storeProfile.customerUniqueReferenceNumber,
      customerUniqueReferenceNumber:
        verificationData?.customerUniqueReferenceNumber ||
        storeProfile.customerUniqueReferenceNumber,
      walletUniqueReferenceNumber: user.walletUniqueReferenceNumber,
      consumerUniqueReferenceNumber: user.consumerUniqueReferenceNumber,
      paymentGatewayReference: verificationData?.paymentGatewayReferenceNumber,
      customerEmailAddress: user.email,
      newCustomer: true,
      verification: {
        ...cardVerificationDetails,
        verificationRequired: false,
        firstName: user.firstName
          ? user.firstName
          : shippingAddress?.firstName
            ? shippingAddress?.firstName
            : "",
        lastName: user.lastName
          ? user.lastName
          : shippingAddress?.lastName
            ? shippingAddress?.lastName
            : "",
      },
      gatewayCustomerId: "",
    });
  };

  const handleAddPaystackCard = () => {
    if (!storeProfile || !user) return;
    initTxMutation.mutate({
      paymentItemUniqueReferenceNumber: crypto.randomUUID(),
      paymentUniqueReferenceNumber: crypto.randomUUID(),
      amount: 10000,
      customerEmail: user.email,
      customerId: "null",
      paymentCurrency: "NGN",
      paymentMethod: "card",
      customerUniqueReferenceNumber: storeProfile.customerUniqueReferenceNumber,
    });
  };

  const isProcessing =
    initTxMutation.isLoading ||
    verifyTxMutation.isLoading ||
    createTokenMutation.isLoading;

  return (
    <Accordion
      expanded={expand}
      onChange={toggleCardExpand}
      sx={{
        background: "#F5F5F6",
        boxShadow: "none",
        borderRadius: "1rem !important",
        padding: "1rem",
        height: "fit-content",
      }}>
      <AccordionSummary
        expandIcon={
          showAdd && methods.length > 0 ? (
            <div
              onClick={(e) => {
                e.stopPropagation();
                setShowAdd(false);
              }}>
              <CloseCircleIcon size={20} />
            </div>
          ) : (
            <BlackCaretDownIcon />
          )
        }
        aria-controls="panel1a-content"
        id="panel1a-header"
        className="!px-0 !min-h-fit"
        sx={{
          "& .MuiAccordionSummary-content": {
            margin: "0 !important",
          },
        }}>
        <h3 className="text-base text-py_black-950 font-medium tracking-[-0.02em] leading-[140%]">
          Payment method
        </h3>
      </AccordionSummary>

      <AccordionDetails className="!p-0">
        <section className="mt-3">
          {/* {isTokenLoading && (
            <div className="w-full h-20 flex flex-col !gap-4 items-center justify-center">
              <CircularProgress size={24} />
              <p className="text-xs lg:text-sm text-py_black-500">
                Loading payment method...
              </p>
            </div>
          )} */}

          {methods.length > 0 && !showAdd && (
            <div className="flex flex-col !gap-4">
              <RadioGroup
                className="flex flex-col w-full !gap-2"
                name="cardToken"
                value={selectedTokenUrn}
                onChange={({ target }) => handleTokenChange(target.value)}>
                {methods.map((token, id: number) => (
                  <li key={id} className="flex items-center w-full">
                    <Radio
                      className="!p-0 !pr-1 md:!p-1 md:!pr-2"
                      value={token.uniqueReferenceNumber}
                    />
                    <div className="inline-flex !gap-4 items-center justify-between text-sm w-full">
                      <h4 className="text-py_black-500 font-medium leading-[140%]">
                        Debit/ Credit Card
                      </h4>

                      <div className="inline-flex items-center !gap-1">
                        {token.cardType?.toLowerCase().includes("visa") && (
                          <img
                            src={images.VisaLogo}
                            alt="visa logo"
                            className="w-[26px] h-[16px] object-cover"
                          />
                        )}
                        {token.cardType?.toLowerCase().includes("verve") && (
                          <img
                            src={images.VerveLogo}
                            alt="verve logo"
                            className="w-[46px] h-[16px] object-cover"
                          />
                        )}
                        {token.cardType
                          ?.toLowerCase()
                          .includes("mastercard") && (
                            <img
                              src={images.MasterCardLogo}
                              alt="mastercard logo"
                              className="w-[26px] h-[16px] object-cover"
                            />
                          )}
                        <span className="text-py_black-950 font-normal leading-[120%]">
                          ***{token?.lastFourDigit}
                        </span>
                        <IconButton
                          size="small"
                          aria-label="Remove card"
                          disabled={
                            removeTokenMutation.isLoading &&
                            removingTokenUrn === token.uniqueReferenceNumber
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveToken(token);
                          }}
                          className="!p-1 !ml-auto !text-red-500 hover:!text-red-600">
                          {removeTokenMutation.isLoading &&
                            removingTokenUrn === token.uniqueReferenceNumber ? (
                            <CircularProgress size={14} />
                          ) : (
                            <FiX size={16} />
                          )}
                        </IconButton>
                      </div>
                    </div>
                  </li>
                ))}
              </RadioGroup>

              <Button
                onClick={() => setShowAdd(true)}
                className="!text-sm !leading-[140%] !text-py_blue-600 !rounded-lg !border !border-solid !border-py_blue-600 !bg-transparent !w-fit !h-8 !normal-case">
                Add payment method
              </Button>
            </div>
          )}

          {/* Add payment method form */}
          {!isTokenLoading && (methods.length === 0 || showAdd) && (
            <>
              <p className="text-sm text-py_black-600">
                Add your payment preference to checkout with
              </p>
              <div className="bg-white rounded-2xl p-4 mt-3">
                {isStripe ? (
                  <>
                    {stripeClientSecret && stripeCustomer ? (
                      <Elements
                        stripe={stripePromise}
                        options={{
                          clientSecret: stripeClientSecret,
                        }}>
                        <AddPayLinkStripePaymentMethod
                          refetchCardToken={refetch}
                          stripeCustomer={stripeCustomer}
                          setupIntentData={stripeIntentData}
                          cardTokenLoading={isTokenLoading || isCustomerLoading || isStripeLoading}
                          openMethodAccordion={() => {
                            setShowAdd(false);
                          }}
                        />
                      </Elements>
                    ) : (
                      <div className="h-[100px] w-full flex items-center justify-center">
                        <CircularProgress />
                      </div>
                    )}
                  </>
                ) : (
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={async (values) => {
                      const data = {
                        ...values,
                        accountName:
                          verifyAccountData?.data?.data?.accountName ?? "",
                      };
                      setCardVerificationDetails(data);
                      handleAddPaystackCard();
                    }}>
                    {({
                      touched,
                      errors,
                      handleBlur,
                      handleSubmit,
                      handleChange,
                      setFieldTouched,
                      setFieldValue,
                      values,
                    }) => (
                      <form
                        className="flex flex-col gap-4 w-full lg:max-w-[320px]"
                        onSubmit={handleSubmit}>
                        <InputBox fullWidth>
                          <Autocomplete
                            options={allBanks}
                            getOptionLabel={(option) => option?.name ?? ""}
                            isOptionEqualToValue={(option, value) =>
                              option.code === value.code
                            }
                            value={
                              allBanks.find(
                                (bank) => bank.name === values.bankName,
                              ) || null
                            }
                            onChange={(_, selectedBank) => {
                              const bankName = selectedBank?.name ?? "";
                              setFieldValue("bankName", bankName);

                              if (values.accountNumber.length >= 10) {
                                handleVerifyAccountNumber(
                                  bankName,
                                  values.accountNumber,
                                );
                              }
                            }}
                            onInputChange={(_, inputValue) => {
                              setFieldValue("bankName", inputValue);
                            }}
                            onBlur={() => {
                              setFieldTouched("bankName", true);
                            }}
                            noOptionsText="No banks found"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                className="input"
                                name="bankName"
                                label="Bank"
                                placeholder="Search bank"
                                InputLabelProps={{
                                  ...(params.InputLabelProps || {}),
                                  shrink: true,
                                }}
                                error={
                                  touched.bankName && Boolean(errors.bankName)
                                }
                              />
                            )}
                          />

                          {touched.bankName && errors.bankName && (
                            <FormHelperText className="feedback">
                              {errors.bankName}
                            </FormHelperText>
                          )}
                        </InputBox>

                        <div className="flex flex-col w-full gap-1">
                          <PyInput
                            fullWidth
                            type="text"
                            label="Account number"
                            placeholder="Enter your bank account number"
                            name="accountNumber"
                            value={values.accountNumber}
                            onChange={(e) => {
                              handleChange(e);
                              handleVerifyAccountNumber(
                                values.bankName,
                                e.target.value,
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.accountNumber &&
                              Boolean(errors.accountNumber)
                            }
                          />

                          {isAccountVerified &&
                            verifyAccountData?.data?.data?.accountName &&
                            values.accountNumber.length >= 10 && (
                              <div className="text-sm text-py_green_2-600 font-medium">
                                {verifyAccountData.data.data.accountName}
                              </div>
                            )}
                        </div>

                        <PySelect
                          fullWidth
                          useValue
                          hasObjects
                          label="Card type"
                          name="cardType"
                          value={values.cardType}
                          selectableObjects={cardTypes}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.cardType && Boolean(errors.cardType)}
                        />

                        <PySelect
                          fullWidth
                          useValue
                          hasObjects
                          label="Card scheme"
                          name="brand"
                          value={values.brand}
                          selectableObjects={cardBrands}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.brand && Boolean(errors.brand)}
                        />

                        <PyInput
                          fullWidth
                          type="text"
                          label="BIN"
                          placeholder="Enter first 6 digits of your card"
                          name="bin"
                          value={values.bin}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.bin && Boolean(errors.bin)}
                        />

                        <div className="flex gap-4 items-center mt-4">
                          {methods.length > 0 && (
                            <PyButton
                              variant="SecondaryOutline"
                              isDisabled={isDisabled}
                              extraClass="!w-[104px] !h-12"
                              click={() => setShowAdd(false)}>
                              Cancel
                            </PyButton>
                          )}
                          <PyButton
                            type="submit"
                            isDisabled={isDisabled}
                            extraClass="!w-fit !h-12"
                            loading={isProcessing}>
                            {isProcessing ? "Processing..." : "Add"}
                          </PyButton>
                        </div>
                      </form>
                    )}
                  </Formik>
                )}
              </div>
            </>
          )}
        </section>
      </AccordionDetails>
    </Accordion>
  );
};
